import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { PAGINATION_CONSTANTS } from '../constants/pagination.constants';
import { EventLogsService } from '../services/api/generated/event-logs/event-logs.service';
import type { EventLogPagination, GetEventLogResourceParams } from '../services/api/generated/model';
import { AuthService } from '../services/auth.service';
import { UserImpersonationService } from '../services/user-impersonation.service';
import { EventLog } from '../types/event-log.interface';
import { LazyLoadParams } from '../types/lazy-load.interface';
import { formatDate } from '../utils/date.utils';
import { extractMultilingualValueString } from '../utils/multilingual.utils';
import { AccessRightDomainStore } from './access-right.store';
import { EventLogTypeStore } from './event-log-type.store';
import { GenericState } from './generic.store';
import { withById, withCaching } from './store.mixins';
import { UserStore } from './user.store';

const MAX_CACHED_ASSETS = 10;
const FAKE_CACHE_ID = 1; // single-bucket cache; the entry key is structural, not asset-bound

interface CachedEventLogData {
  eventLogs: EventLog[];
  pagination: PaginationState;
  lastAccessed: number;
}

interface EventLogBaseState extends GenericState<EventLog> {
  eventLogsCache: Record<number, CachedEventLogData>;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
}

const initialBaseState: EventLogBaseState = {
  entities: [],
  selectedId: null,
  cache: {},
  lastUpdated: 0,
  eventLogsCache: {},
};

const initialPaginationState: PaginationState = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  limit: PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
};

type PlaceholderValue = string | number | { [lang: string]: string };

function replacePlaceholders(
  template: string,
  data: Record<string, PlaceholderValue>,
  activeLang: string,
  assetId: number = -1,
  withURL: boolean = false,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(data, key)) {
      return match;
    }
    const raw = data[key];
    if (!withURL) return String(raw);
    const campaignId = data['campaign_id'];
    const url = campaignId !== undefined ? `/assets/${assetId}/monitoring/campaigns/${String(campaignId)}/measures` : '';
    switch (key) {
      case 'campaign_name':
        return `###${url}###${formatDate(raw as string | Date)}###`;
      case 'measure_name': {
        const measure_name = extractMultilingualValueString(raw as string, raw as string, activeLang);
        return `###${url}###${measure_name}###`;
      }
      default:
        return String(raw);
    }
  });
}

const toQueryParams = (params: LazyLoadParams): GetEventLogResourceParams => ({
  page: params.pagination.page,
  pageSize: params.pagination.pageSize,
  ...(params.sorts?.length ? { sorts: JSON.stringify(params.sorts) } : {}),
  ...(params.filters?.length ? { filters: JSON.stringify(params.filters) } : {}),
});

// Event logs are read/delete-only on the API (no POST/PUT/getById), so withCrudMethods is bypassed
// in favour of a direct Orval injection focused on the paginated list endpoint.
export const EventLogStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialBaseState),
  withCaching<EventLog>(10 * 60 * 1000),
  withById<EventLog>(),
  withState({ paginationState: initialPaginationState }),
  withMethods(store => {
    const api = inject(EventLogsService);
    const eventLogsTypeStore = inject(EventLogTypeStore);
    const translateService = inject(TranslocoService);
    const impersonationService = inject(UserImpersonationService);
    const accessRightStore = inject(AccessRightDomainStore);
    const userStore = inject(UserStore);
    const authService = inject(AuthService);

    const evictLRUIfNeeded = (currentCache: Record<number, CachedEventLogData>): Record<number, CachedEventLogData> => {
      const cacheEntries = Object.entries(currentCache);
      if (cacheEntries.length < MAX_CACHED_ASSETS) {
        return currentCache;
      }
      const sortedByAccess = cacheEntries.sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed);
      const oldest = sortedByAccess[0];
      if (!oldest) return currentCache;
      const lruAssetId = Number(oldest[0]);
      const { [lruAssetId]: removed, ...remaining } = currentCache;
      return remaining;
    };

    const enrichEventLogs = (eventLogs: EventLog[]): EventLog[] => {
      const lookupType = eventLogsTypeStore.byId();
      return eventLogs.flatMap(el => {
        const type = lookupType(el.eventLogTypeId);
        if (!type) return [];
        el.title = type.name;
        const meta = JSON.parse(el.metadata) as Record<string, PlaceholderValue>;
        el.message = replacePlaceholders(type.messageTemplate, meta, translateService.getActiveLang(), el.assetId, true);
        el.level = type.level;
        return [el];
      });
    };

    const filterEventLogs = (eventLogs: EventLog[]): EventLog[] => {
      const realUserId = authService.currentUserId();
      const effectiveUserId = impersonationService.getEffectiveUserId(realUserId);

      if (!effectiveUserId) {
        return eventLogs;
      }

      if (!impersonationService.isImpersonating()) {
        const user = userStore.byId()(effectiveUserId);
        if (user?.isAdmin) {
          return eventLogs;
        }
      }

      const userAccessRights = accessRightStore.entities().filter(ar => ar.userId === effectiveUserId);
      const allowedAssetIds = new Set(userAccessRights.map(ar => ar.assetId));

      return eventLogs.filter(event => allowedAssetIds.has(event.assetId));
    };

    return {
      async loadPaginatedEntities(params: LazyLoadParams): Promise<void> {
        await eventLogsTypeStore.loadAll();
        const response = await firstValueFrom(api.getEventLogResource<EventLogPagination>(toQueryParams(params)));
        const eventLogs = enrichEventLogs(filterEventLogs(response['event-logs'] as EventLog[]));

        const paginationState: PaginationState = {
          currentPage: response.page,
          totalPages: response.pages,
          totalItems: response.total,
          limit: response.limit,
        };

        const cleanedCache = evictLRUIfNeeded(store.eventLogsCache());

        patchState(store, {
          entities: eventLogs,
          eventLogsCache: {
            ...cleanedCache,
            [FAKE_CACHE_ID]: { eventLogs, pagination: paginationState, lastAccessed: Date.now() },
          },
          lastUpdated: Date.now(),
          paginationState,
        });
      },

      eventLogs: (): EventLog[] => store.eventLogsCache()[FAKE_CACHE_ID]?.eventLogs ?? [],
    };
  }),
);
